
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { userChangePassword } from '../features/auth/authAction';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    IconButton,
    Alert,
    CircularProgress
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';

const ChangePassword = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const { loading, error, success } = useSelector((state) => state.auth);
    const { register, handleSubmit, reset, formState: { errors }, watch } = useForm();

    // State to toggle password visibility
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const onSubmit = (data) => {
        dispatch(userChangePassword({
            currentPassword: data.currentPassword,
            newPassword: data.newPassword
        })).then((res) => {
            if (res.meta.requestStatus === 'fulfilled') {
                reset(); // Clear form on success
            }
        });
    };

    return (
        <Box sx={{
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4
        }}>
            <Container maxWidth="sm">
                <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 600, mb: 3 }}>
                            Change Password
                        </Typography>

                        {success && <Alert severity="success" sx={{ mb: 2 }}>Password changed successfully!</Alert>}
                        {/* Error handling is done via global alert but we can also show inline if needed */}

                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/* Current Password */}
                            <TextField
                                fullWidth
                                label="Current Password"
                                type={showCurrentPassword ? 'text' : 'password'}
                                margin="normal"
                                {...register('currentPassword', { required: 'Current password is required' })}
                                error={!!errors.currentPassword}
                                helperText={errors.currentPassword?.message}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle current password visibility"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                edge="end"
                                            >
                                                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            {/* New Password */}
                            <TextField
                                fullWidth
                                label="New Password"
                                type={showNewPassword ? 'text' : 'password'}
                                margin="normal"
                                {...register('newPassword', {
                                    required: 'New password is required',
                                    minLength: { value: 6, message: 'Password must be at least 6 characters' }
                                })}
                                error={!!errors.newPassword}
                                helperText={errors.newPassword?.message}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle new password visibility"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                edge="end"
                                            >
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            {/* Confirm New Password */}
                            <TextField
                                fullWidth
                                label="Confirm New Password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                margin="normal"
                                {...register('confirmPassword', {
                                    required: 'Please confirm your new password',
                                    validate: (val) => {
                                        if (watch('newPassword') != val) {
                                            return "Your passwords do not match";
                                        }
                                    }
                                })}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword?.message}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle confirm password visibility"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                sx={{ mt: 3, mb: 1, py: 1.5, borderRadius: 2 }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default ChangePassword;
